import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useIsMobile } from "@/hooks/use-mobile";
import { AD_CLIENT, adDensityForPath, adsAllowedNow, personalizedAdsAllowed } from "@/lib/ads-policy";
import {
  AD_RESERVED_HEIGHT,
  MANUAL_ADS_ON,
  slotFor,
  type AdVariant,
} from "@/lib/ads-config";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

/**
 * Manuell AdSense-slot med lazy-loading (IntersectionObserver), reserverad
 * höjd (skyddar CLS) och automatisk kollaps om annonsen inte fylls.
 *
 * Renderar inget alls om master-brytaren är av eller slot-ID saknas —
 * inget bryts innan AdSense-godkännandet är klart.
 */
export function AdSlot({
  slot,
  variant = "in-article",
  className = "",
  label = "Annons",
}: {
  slot?: string;
  variant?: AdVariant;
  className?: string;
  label?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const insRef = useRef<HTMLModElement>(null);
  const [visible, setVisible] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [unfilled, setUnfilled] = useState(false);
  const pushed = useRef(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const resolvedSlot = MANUAL_ADS_ON ? slotFor(variant, slot) : "";

  // Annonspolicy: bara sidor med samtycke, index-status och riktigt innehåll.
  useEffect(() => {
    setAllowed(false);
    setUnfilled(false);
    const t = window.setTimeout(() => setAllowed(adsAllowedNow(pathname)), 950);
    return () => window.clearTimeout(t);
  }, [pathname]);

  useEffect(() => {
    if (!resolvedSlot || !allowed) return;
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [resolvedSlot, allowed]);

  useEffect(() => {
    if (!visible || pushed.current) return;
    pushed.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push(
        personalizedAdsAllowed() ? {} : { requestNonPersonalizedAds: 1 },
      );
    } catch (e) {
      console.debug("AdSense push failed", e);
    }
  }, [visible]);

  // Kollapsa ytan helt om Google inte fyllde annonsen.
  useEffect(() => {
    if (!visible) return;
    const check = () => {
      const status = insRef.current?.getAttribute("data-ad-status");
      if (status === "unfilled") setUnfilled(true);
    };
    const t1 = window.setTimeout(check, 2500);
    const t2 = window.setTimeout(check, 6000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [visible]);

  if (!resolvedSlot || !allowed || unfilled) return null;

  const format =
    variant === "multiplex"
      ? "autorelaxed"
      : variant === "in-article" || variant === "in-article-2" || variant === "in-feed"
        ? "fluid"
        : variant === "display" || variant === "display-horizontal"
          ? "auto"
          : undefined;
  const layout =
    variant === "in-article" || variant === "in-article-2"
      ? "in-article"
      : variant === "in-feed"
        ? "in-feed"
        : undefined;
  const fullWidthResponsive = variant === "display" || variant === "display-horizontal";
  const isMobileFixed = variant === "mobile-fixed";

  const insStyle = isMobileFixed
    ? ({ display: "inline-block", width: "320px", height: "100px" } as const)
    : ({ display: "block", textAlign: "center" } as const);
  const insClassName = isMobileFixed ? "adsbygoogle" : "adsbygoogle block";

  return (
    <aside
      ref={containerRef}
      className={`my-10 ${className}`}
      aria-label={label || "Annons"}
      data-nosnippet
      data-ad-variant={variant}
      style={{ minHeight: visible ? AD_RESERVED_HEIGHT[variant] : undefined }}
    >
      {label && (
        <p className="mb-2 text-center text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground/70">
          {label}
        </p>
      )}
      {visible && (
        <ins
          ref={insRef}
          className={insClassName}
          style={insStyle}
          data-ad-client={AD_CLIENT}
          data-ad-slot={resolvedSlot}
          {...(layout ? { "data-ad-layout": layout } : {})}
          {...(format ? { "data-ad-format": format } : {})}
          {...(fullWidthResponsive ? { "data-full-width-responsive": "true" } : {})}
        />
      )}
    </aside>
  );
}

/**
 * Sticky-annons för desktop (>=1024px). Följer med scroll i högerkolumnen.
 * Döljs helt på mobil för att inte täcka innehåll.
 */
export function StickyDesktopAd({ className = "" }: { className?: string }) {
  if (!MANUAL_ADS_ON || !slotFor("sticky")) return null;
  return (
    <div className={`hidden lg:block ${className}`}>
      <div className="sticky top-24">
        <AdSlot variant="sticky" label="Annons" />
      </div>
    </div>
  );
}

/**
 * Annonser i sidfoten. Densitetsstyrt: översiktssidor (start, fordon) får en
 * enda display-annons, redaktionella sidor får även en multiplex-enhet med
 * relaterat innehåll. Renderas aldrig på blockerade eller tunna sidor.
 *
 * På mobil används den fasta 320×100-enheten istället för responsiv display,
 * så vi får en kontrollerad, liten banneryta där utrymmet är som trängst.
 */
export function FooterAds() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const density = adDensityForPath(pathname);
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!MANUAL_ADS_ON || density === "off") return null;
  if (!mounted) return null;

  return (
    <div className="container-edge pt-10">
      {isMobile ? (
        <AdSlot variant="mobile-fixed" label="" className="my-0" />
      ) : (
        <AdSlot variant="display" label="Annons" className="my-0" />
      )}
      {density === "high" && (
        <AdSlot variant="multiplex" label="Relaterat innehåll" className="mt-8" />
      )}
    </div>
  );
}
